// utils/jdOwnership.js
//
// JD Ownership (Ownership design v2.3, parts 1, 2 and 3, sections 12 and 13; wireframe jobs-library-ownership-v3.html:
// the JD page header's "Current Owner" row and the Edit / Revise tooltips). Only the person who holds the JD
// (its owner, or its delegate while it is delegated) acts on it; an Unclaimed JD (a system upload) is claimed
// by the first person to save an edit, revise, send or publish it. Pure helpers, so the row, the tooltips and
// the NOT_HOLDER refusal are tested without rendering the page. The server makes the final call (NOT_HOLDER).

import { JD_STATUSES } from 'constants/jobs.constants';
import { OUT_OF_SCOPE_MESSAGE, OUT_OF_SCOPE_REASON } from 'Utils/jobDetailsPageState';

// ── Wording (wireframe) ───────────────────────────────────────────────────────
export const CURRENT_OWNER_LABEL = 'Current Owner:';
export const UNCLAIMED_LABEL = 'Unclaimed';
export const TOOLTIP_UNCLAIMED = 'Unclaimed JD (system upload). The first person to save an edit, revise, send or '
  + 'publish it becomes its permanent owner. Opening the editor does not claim it.';
export const TOOLTIP_EDIT_UNCLAIMED = 'Unclaimed JD – saving your edits (or sending / publishing) makes you its owner.';
export const TOOLTIP_REVISE_UNCLAIMED = 'Unclaimed JD – revising it makes you its owner.';
export const TOOLTIP_SHARED_NO_EDIT = 'The drafts are shared with you: you can view them and comment on the changes, '
  + 'but not edit.';
export const YOU_SUFFIX = '(You)';
// The Unpublish confirmation; on an Unclaimed JD it says that unpublishing claims it (Unclaimed tooltip wording)
export const UNPUBLISH_CONFIRM_MESSAGE = 'JD can be modified once unpublished.';
export const UNPUBLISH_UNCLAIMED_NOTE = 'Unclaimed JD – unpublishing it makes you its permanent owner.';
export const getUnpublishConfirmMessage = (jdOwnership) => (jdOwnership?.isUnowned
  ? `${UNPUBLISH_CONFIRM_MESSAGE} ${UNPUBLISH_UNCLAIMED_NOTE}`
  : UNPUBLISH_CONFIRM_MESSAGE);

// A write refused because the caller does not hold the JD (e.g. a stale page after a handover)
export const NOT_HOLDER = 'NOT_HOLDER';
export const NOT_HOLDER_TITLE = 'You no longer hold this JD';
export const NOT_HOLDER_MESSAGE = 'Someone else holds this JD now, so nothing was changed. The page has been '
  + 'reloaded to show its current owner.';

const normEmail = (value) => (typeof value === 'string' ? value.trim().toLowerCase() : '');

// "Only the JD owner (Robert Scott) and their delegates can act on this JD." (or the shared-draft text). While the
// JD is delegated only the delegate acts on it, so that is what everyone else (the owner included) is told.
export const getNotHolderTooltip = (ownerName, isSharedWith = false, delegateName = '') => {
  if (isSharedWith) return TOOLTIP_SHARED_NO_EDIT;
  if (delegateName) return `Only ${delegateName} (delegate) can act on this JD while it is delegated.`;
  return `Only the JD owner (${ownerName || 'its owner'}) and their delegates can act on this JD.`;
};

// The ownership facts of the /status_based_job_description response, as the page keeps them
export const pickOwnershipFacts = (response) => ({
  ownerEmail: response?.owner_email ?? null,
  holder: response?.holder ?? null,
  isDelegate: response?.is_delegate === true,
  isSharedWith: response?.is_shared_with === true,
  // the delegate holding the JD now (a deactivated one is never sent) and their name
  delegateEmail: response?.delegate_email || '',
  delegateName: response?.delegate_name || null,
  // the people the drafts are shared with: [{ email, name }]
  shares: Array.isArray(response?.shares) ? response.shares.filter((p) => p?.email) : [],
});

// Does the viewer hold the JD? The delegate while delegated, otherwise the owner. Read from the backend's
// facts (holder, is_delegate), never by matching names; a response without `holder` (an older backend)
// counts the owner as the holder.
export const isJdHolder = (jdOwnership) => {
  if (!jdOwnership) return false;
  if (jdOwnership.isDelegate) return true;
  if (!jdOwnership.isOwner) return false;
  return jdOwnership.holder === null || jdOwnership.holder === undefined
    || normEmail(jdOwnership.holder) === normEmail(jdOwnership.ownerEmail);
};

// The header row: { unclaimed, name, isYou }. Without ownership facts (e.g. a license preview) the page's
// owner name is shown as before; nothing at all when there is neither.
export const getCurrentOwnerRow = (jdOwnership, fallbackName) => {
  if (jdOwnership?.isUnowned) return { unclaimed: true, name: '', isYou: false };
  const name = jdOwnership?.ownerName || fallbackName || '';
  if (!name) return null;
  return { unclaimed: false, name, isYou: jdOwnership?.isOwner === true };
};

// Edit / Revise on an Unclaimed JD the viewer may act on: acting makes them its owner. null otherwise.
export const getUnclaimedTooltip = (action, jdOwnership) => {
  if (!jdOwnership?.isUnowned || !jdOwnership.permissions?.[action]?.allowed) return null;
  if (action === 'can_edit') return TOOLTIP_EDIT_UNCLAIMED;
  if (action === 'can_revise') return TOOLTIP_REVISE_UNCLAIMED;
  return null;
};

// Edit and Revise are shown only to whoever may act on the JD (wireframe: canActOn || unclaimed), and never while
// it is published or awaiting approval. Without ownership facts (e.g. a license preview) the page's own lock decides.
export const showsEditAndRevise = ({ jdOwnership, familyStatus, isLockedByOther = false }) => {
  if (familyStatus === JD_STATUSES.PUBLISHED || familyStatus === JD_STATUSES.PENDING_APPROVAL) return false;
  if (!jdOwnership) return !isLockedByOther;
  return isJdHolder(jdOwnership) || jdOwnership.isUnowned === true;
};

// A Jobs Library row: may this viewer act on it (e.g. Retry)? Its holder (holder_email: the delegate while
// delegated, else the owner), or anyone on an Unclaimed JD. A row without holder_email falls back to its owner.
export const isRowHolderOrUnclaimed = (record, viewerEmail) => {
  const holder = normEmail(record?.holder_email ?? record?.owner_email);
  return !holder || holder === normEmail(viewerEmail);
};

// A refusal (an axios error) because the caller does not hold the JD
export const isNotHolderError = (error) => error?.response?.data?.reason === NOT_HOLDER;

// A save that would take the JD out of the view scope of someone working on it (Ownership design, part 8). The
// server's message names who; this is only the fallback when it sends none.
export const JD_SCOPE_EDIT_BLOCKED = 'JD_SCOPE_EDIT_BLOCKED';
export const JD_SCOPE_EDIT_BLOCKED_MESSAGE = 'This change would take the JD out of the view scope of people working '
  + 'on it. Keep its legal entity, business unit, function and sub-function, or ask an HR Admin to change their '
  + 'access first.';

// The message for an ownership or view-scope refusal ({ reason, message } from the server); null for any other.
const refusalMessage = (body) => {
  switch (body?.reason) {
    case NOT_HOLDER: return NOT_HOLDER_MESSAGE;
    case OUT_OF_SCOPE_REASON: return OUT_OF_SCOPE_MESSAGE;
    case JD_SCOPE_EDIT_BLOCKED: return body.message || JD_SCOPE_EDIT_BLOCKED_MESSAGE;
    default: return null;
  }
};

// The same for a failed request (an axios error)
export const getOwnershipRefusalMessage = (error) => refusalMessage(error?.response?.data);

// A Revise the backend refused reaches the Jobs Library over the task stream, not as an HTTP error: the task
// processor posts { progress: 30, error: <the backend's response, as a JSON string> }. Which refusal it was and
// what to say: { reason, message } for NOT_HOLDER, STALE, OUT_OF_SCOPE or JD_SCOPE_EDIT_BLOCKED; null for anything
// else (e.g. a duplicate JD) or an error that can't be read.
export const STALE = 'STALE';
export const REVISE_STALE_MESSAGE = 'This JD has changed since you opened it, so the Revise did not start. '
  + 'Open it again to see the latest version.';
export const getStreamRefusal = (streamEvent) => {
  let body = streamEvent?.error;
  if (typeof body === 'string') {
    try {
      body = JSON.parse(body);
    } catch {
      return null;
    }
  }
  if (!body || typeof body !== 'object') return null;
  const message = body.reason === STALE ? REVISE_STALE_MESSAGE : refusalMessage(body);
  return message ? { reason: body.reason, message } : null;
};

// ── Delegation (Ownership design, part 2; wireframe: the header's "Delegated to" row and its picker) ──────
export const DELEGATED_TO_LABEL = 'Delegated to:';
export const NO_ONE_LABEL = 'No one';
export const TOOLTIP_DELEGATE_ADD = 'Temporary handover: only this person can act on this JD until you take it back';
export const TOOLTIP_RELINQUISH = 'Relinquish control – hand the JD back to the owner';
export const getTakeBackTooltip = (name) => `Take back control from ${name}`;
// The actor's short on-screen confirmations (everyone else gets a bell notification)
export const getDelegatedToast = (name) => `${name} now has control of this JD until they relinquish it or you take it back.`;
export const getRelinquishedToast = (ownerName) => `Control handed back to ${ownerName || 'the owner'}.`;
export const getTakenBackToast = (jdLabel) => `You have control of ${jdLabel || 'this JD'} again.`;
// Take back asks first (not in the wireframe: decided 2026-11-02)
export const getTakeBackConfirm = (name) => ({
  title: `Take back control from ${name || 'the delegate'}?`,
  content: 'They will no longer be able to edit or act on this JD.',
  okText: 'Take back',
  cancelText: 'Cancel',
});
// The delegate's own × asks first, like Take back (decided 2026-11-03)
export const getRelinquishConfirm = (ownerName) => ({
  title: `Hand control back to ${ownerName || 'the owner'}?`,
  content: 'You will no longer be able to edit or act on this JD.',
  okText: 'Hand back',
  cancelText: 'Cancel',
});
export const DELEGATE_PICKER = {
  title: 'Delegate this JD',
  label: 'Delegate to',
  placeholder: 'Search people by name or role…',
  delegateLabel: 'Delegate',
  cancelLabel: 'Cancel',
  // Not in the wireframe; the design says "the picker says no one has access" (section 13)
  noOneMessage: 'No one who can edit JDs has access to this JD, so it can\'t be delegated.',
  loadFailedMessage: "The list of people couldn't be loaded. Close this and try again.",
};
export const DELEGATION_FAILED_TITLE = 'Control not changed';
const DELEGATION_FALLBACK = 'The change could not be made. Please try again.';
// The refusals after which the page reloads to show who has control now
const DELEGATION_RELOAD_REASONS = new Set(['ALREADY_DELEGATED', 'NOT_DELEGATED', 'NOT_DELEGATE', 'NOT_OWNER',
  'NOT_HOLDER', 'STALE', OUT_OF_SCOPE_REASON, 'NOT_FOUND']);

// The "Delegated to" row: null when not shown (no delegate, and the viewer is not the owner who could add one).
// The owner (with the edit right) takes back with × and adds with +; the delegate relinquishes with × on their own
// chip; nobody manages it while editing.
export const getDelegatedRow = (jdOwnership, { canManage = false, isEditing = false } = {}) => {
  if (!jdOwnership) return null;
  const delegate = jdOwnership.delegateEmail
    ? { name: jdOwnership.delegateName || jdOwnership.delegateEmail, isYou: jdOwnership.isDelegate === true }
    : null;
  const ownerManages = jdOwnership.isOwner === true && canManage && !isEditing;
  if (!delegate && !ownerManages) return null;
  return {
    delegate,
    canTakeBack: !!delegate && ownerManages,
    canRelinquish: !!delegate && delegate.isYou && !isEditing,
    canAdd: !delegate && ownerManages,
  };
};

// A failed delegate / take back / relinquish (an axios error): { message, reload }
export const getDelegationError = (error) => {
  const data = error?.response?.data || {};
  return {
    message: refusalMessage(data) || data.message || DELEGATION_FALLBACK,
    reload: DELEGATION_RELOAD_REASONS.has(data.reason),
  };
};

// ── Draft sharing (Ownership design, part 3; wireframe: the header's "Draft shared with" row and its picker) ──
// Whoever holds the JD shares its private drafts with up to 10 people, who read them and comment on the changes
// (Draft Feedback), but never edit.
export const SHARED_WITH_LABEL = 'Draft shared with:';
export const SHARE_LIMIT = 10;
// The ownership confirmations (delegate, take back, hand back, share, stop sharing, comments sent, comment resolved,
// transfer, reassign approver) stay up
// 2 seconds instead of SbNotification's 4; the rest of the app keeps its default
export const OWNERSHIP_TOAST_CONFIG = { duration: 2 };
// The header shows this many people the drafts are shared with; the rest fold behind "+N more"
export const SHARED_SHOWN = 2;
export const getMoreLabel = (n) => `+${n} more`;
export const SHOW_LESS_LABEL = 'Show less';
// The viewer first when they are one of them, so their "(You)" chip is never folded away
export const orderSharedPeople = (people) => [...(people || [])].sort((a, b) => Number(!!b.isYou) - Number(!!a.isYou));
export const TOOLTIP_SHARE_ADD = 'Share drafts with up to 10 people. They can view and comment, but not edit';
export const getStopSharingTooltip = (name) => `Stop sharing drafts with ${name}`;
// The actor's short on-screen confirmations (the person, and the owner when the delegate acted, get a bell
// notification from the server)
export const getSharedToast = (name) => `Drafts shared with ${name} (view and comment).`;
export const getUnsharedToast = (name) => `Draft share revoked for ${name}.`;
// Removing a share (×) asks first, like Take back (decided 2026-11-03)
export const getStopSharingConfirm = (name) => ({
  title: `Stop sharing drafts with ${name || 'this person'}?`,
  content: 'They will no longer be able to view the drafts or comment on them.',
  okText: 'Stop sharing',
  cancelText: 'Cancel',
});
export const SHARE_PICKER = {
  title: 'Share drafts',
  label: 'Share with',
  placeholder: 'Search people by name or role…',
  shareLabel: 'Share',
  cancelLabel: 'Cancel',
  // Not in the wireframe (its list simply comes up empty)
  noOneMessage: 'No one else who can edit JDs has access to this JD, so its drafts can\'t be shared.',
  loadFailedMessage: "The list of people couldn't be loaded. Close this and try again.",
};
export const SHARE_FAILED_TITLE = 'Sharing not changed';
const SHARE_FALLBACK = 'The change could not be made. Please try again.';
// The refusals after which the page reloads to show the shares as they are now
const SHARE_RELOAD_REASONS = new Set(['SHARE_NOT_ALLOWED', 'SHARE_LIMIT_REACHED', 'ALREADY_SHARED', 'NOT_SHARED', 'INVALID_SHARE',
  'NOT_HOLDER', 'STALE', OUT_OF_SCOPE_REASON, 'NOT_FOUND']);

// The "Draft shared with" row: null when not shown (nobody shared with, and the viewer can't add anyone). Whoever
// holds the JD (with the edit right, not while editing) removes with × and adds with + while under 10.
// No sharing while the JD is in review or archived (part 5, section 6: shares end when it is sent); × stays, so a
// leftover share can always be removed. The holder is told why there is no + (not in the wireframe).
const NO_SHARING_STATUSES = new Set([JD_STATUSES.PENDING_APPROVAL, JD_STATUSES.NEEDS_REVISION, 'ARCHIVED']);
export const TOOLTIP_SHARE_BLOCKED = 'Drafts can\'t be shared while the JD is awaiting approval, needs revision or is '
  + 'archived.';
export const getSharedRow = (jdOwnership, { canManage = false, isEditing = false, viewerEmail = '' } = {}) => {
  if (!jdOwnership) return null;
  const you = normEmail(viewerEmail);
  const people = (jdOwnership.shares || []).map((p) => ({
    email: p.email, name: p.name || p.email, isYou: Boolean(you) && normEmail(p.email) === you,
  }));
  const manages = isJdHolder(jdOwnership) && canManage && !isEditing;
  if (!people.length && !manages) return null;
  const blocked = manages && NO_SHARING_STATUSES.has(jdOwnership.familyStatus);
  return {
    people,
    canRemove: manages,
    canAdd: manages && !blocked && people.length < SHARE_LIMIT,
    blockedTooltip: blocked ? TOOLTIP_SHARE_BLOCKED : null,
  };
};

// A failed share / unshare / picker load (an axios error): { message, reload }. The server's message names the
// person (e.g. not in their view scope); a NOT_HOLDER says the page is stale.
export const getShareError = (error) => {
  const data = error?.response?.data || {};
  return {
    message: (data.reason === NOT_HOLDER ? NOT_HOLDER_MESSAGE : data.message) || refusalMessage(data) || SHARE_FALLBACK,
    reload: SHARE_RELOAD_REASONS.has(data.reason),
  };
};
